import type { ClassroomSession, SessionDate } from "./types";

// Where the booked class is in time, the way v1's useRunningStatusState
// (apps/widget/src/libs/hooks/useRunningStatusState.js) works it out: it
// decides whether "Join" is live, whether the post-class tab unlocks, and
// whether the receipt gives way to "Book again". Pure, so the clock is an
// argument.

export type RunningStatus = "yetToRun" | "startingSoon" | "running" | "finished";

/** Joinable from ten minutes before the start, like v1. */
const STARTING_SOON_MS = 10 * 60 * 1000;

const endOf = (d: SessionDate) => new Date(d.startDateTime).getTime() + d.duration * 60 * 1000;

/** The occurrence the page is about: the session's own date, or, for a
 * session with later dates, the first one that hasn't ended. Once every
 * date has ended it is the last one. */
export function currentOccurrence(session: ClassroomSession, now: number): SessionDate {
  let date: SessionDate = { startDateTime: session.startDateTime, duration: session.duration };
  for (const next of session.additionalClasses ?? []) {
    if (endOf(date) >= now) break;
    date = { startDateTime: next.startDateTime, duration: next.duration };
  }
  return date;
}

export function runningStatusAt(date: SessionDate, now: number): RunningStatus {
  const elapsed = now - new Date(date.startDateTime).getTime();
  if (elapsed > date.duration * 60 * 1000) return "finished";
  if (elapsed > 0) return "running";
  if (elapsed > -STARTING_SOON_MS) return "startingSoon";
  return "yetToRun";
}

/** Milliseconds until the status next changes; null once finished. */
export function msUntilNextStatus(date: SessionDate, now: number): number | null {
  const start = new Date(date.startDateTime).getTime();
  switch (runningStatusAt(date, now)) {
    case "yetToRun":
      return start - STARTING_SOON_MS - now + 1;
    case "startingSoon":
      return start - now + 1;
    case "running":
      return endOf(date) - now + 1;
    case "finished":
      return null;
  }
}

export const isJoinable = (status: RunningStatus) => status === "startingSoon" || status === "running";
